import { z } from "zod";
import { OPS, type Op } from "../../content/modes";
import { randInt, type Rng } from "../rng";
import {
  apply,
  factKey,
  SLOT,
  TIERS,
  type Question,
  type Sym,
  type Tier,
  type Token,
} from "./question";

/*
  QUESTION TYPES. One row per kind of question. To add a type, add a row:
  the id, strand, tier (1 easiest to 5 hardest), a parent-facing label, the
  limit Practice checks against the parent's range settings, and a generator.

  limit:  for + and −, the largest number that appears (checked against
          "adding up to"); for × and ÷, the largest times table used
          (checked against "tables up to").
  points: equal to the tier for now; Battles and Quests use them later.
*/

type Built = Pick<Question, "tokens" | "answer" | "factKey">;

export interface QuestionType {
  id: string;
  op: Op;
  tier: Tier;
  points: number;
  label: string;
  limit: number;
  prereqs: string[];
  gen: (rng: Rng) => Built;
}

/** Where the gap goes: 0 = first number, 1 = second number, 2 = the answer. */
type Gap = 0 | 1 | 2;

/** a sym b = c, with the gap at `gap`. */
function binary(sym: Sym, a: number, b: number, gap: Gap = 2): Built {
  const c = apply(sym, a, b);
  const nums = [a, b, c] as const;
  const tokens: Token[] = [
    gap === 0 ? SLOT : a,
    sym,
    gap === 1 ? SLOT : b,
    "=",
    gap === 2 ? SLOT : c,
  ];
  return { tokens, answer: nums[gap], factKey: factKey(a, sym, b) };
}

/** a s1 b s2 c = ? */
function twoStep(a: number, s1: Sym, b: number, s2: Sym, c: number): Built {
  const answer = apply(s2, apply(s1, a, b), c);
  return { tokens: [a, s1, b, s2, c, "=", SLOT], answer, factKey: `${a}${s1}${b}${s2}${c}` };
}

/** The pair in either order: 3 × 7 or 7 × 3. */
function eitherOrder(rng: Rng, a: number, b: number): [number, number] {
  return rng.next() < 0.5 ? [a, b] : [b, a];
}

/** The ×t or ÷t table, 0 to 10. */
function table(op: "mul" | "div", t: number, tier: Tier): Omit<QuestionType, "points"> {
  return {
    id: `${op}.x${t}`,
    op,
    tier,
    label: op === "mul" ? `${t} times table` : `Sharing by ${t}`,
    limit: t,
    prereqs: [],
    gen: (rng) => {
      const n = randInt(rng, 0, 10);
      if (op === "div") return binary("÷", t * n, t);
      const [a, b] = eitherOrder(rng, t, n);
      return binary("×", a, b);
    },
  };
}

const DEFS: Omit<QuestionType, "points">[] = [
  // ── Adding ─────────────────────────────────────────────────────────────
  {
    id: "add.to10",
    op: "add",
    tier: 1,
    label: "Adding to 10",
    limit: 10,
    prereqs: [],
    gen: (rng) => {
      const a = randInt(rng, 0, 10);
      return binary("+", a, randInt(rng, 0, 10 - a));
    },
  },
  {
    id: "add.plus12",
    op: "add",
    tier: 1,
    label: "Adding 1 or 2",
    limit: 10,
    prereqs: [],
    gen: (rng) => {
      const [a, b] = eitherOrder(rng, randInt(rng, 0, 8), randInt(rng, 1, 2));
      return binary("+", a, b);
    },
  },
  {
    id: "add.doubles",
    op: "add",
    tier: 1,
    label: "Doubles to 10 + 10",
    limit: 20,
    prereqs: [],
    gen: (rng) => {
      const n = randInt(rng, 1, 10);
      return binary("+", n, n);
    },
  },
  {
    id: "add.to20",
    op: "add",
    tier: 2,
    label: "Adding to 20",
    limit: 20,
    prereqs: ["add.to10"],
    gen: (rng) => {
      const sum = randInt(rng, 11, 20);
      const a = randInt(rng, sum - 10, 10);
      return binary("+", a, sum - a);
    },
  },
  {
    id: "add.missingTo20",
    op: "add",
    tier: 3,
    label: "Missing number to 20",
    limit: 20,
    prereqs: ["add.to10"],
    gen: (rng) => {
      const c = randInt(rng, 5, 20);
      const a = randInt(rng, 0, c);
      return binary("+", a, c - a, rng.next() < 0.5 ? 0 : 1);
    },
  },
  {
    id: "add.nearDoubles",
    op: "add",
    tier: 2,
    label: "Near doubles",
    limit: 20,
    prereqs: ["add.doubles"],
    gen: (rng) => {
      const n = randInt(rng, 1, 9);
      const [a, b] = eitherOrder(rng, n, n + 1);
      return binary("+", a, b);
    },
  },
  {
    id: "add.2d1d",
    op: "add",
    tier: 3,
    label: "2-digit + 1-digit, carrying",
    limit: 100,
    prereqs: ["add.to20"],
    gen: (rng) => {
      const a = randInt(rng, 1, 8) * 10 + randInt(rng, 2, 9);
      const b = randInt(rng, 10 - (a % 10), 9);
      return binary("+", a, b);
    },
  },
  {
    id: "add.tens",
    op: "add",
    tier: 3,
    label: "Adding tens",
    limit: 100,
    prereqs: ["add.to10"],
    gen: (rng) => {
      const a = randInt(rng, 1, 9);
      return binary("+", a * 10, randInt(rng, 1, 10 - a) * 10);
    },
  },
  {
    id: "add.2d2d",
    op: "add",
    tier: 4,
    label: "2-digit + 2-digit",
    limit: 100,
    prereqs: ["add.2d1d", "add.tens"],
    gen: (rng) => {
      const a = randInt(rng, 10, 89);
      return binary("+", a, randInt(rng, 10, 99 - a));
    },
  },
  {
    id: "add.missingTo100",
    op: "add",
    tier: 4,
    label: "Missing number to 100",
    limit: 100,
    prereqs: ["add.missingTo20", "add.2d1d"],
    gen: (rng) => {
      const c = randInt(rng, 20, 100);
      const a = randInt(rng, 10, c - 1);
      return binary("+", a, c - a, rng.next() < 0.5 ? 0 : 1);
    },
  },
  {
    id: "add.twoStep",
    op: "add",
    tier: 5,
    label: "Adding three numbers",
    limit: 50,
    prereqs: ["add.to20"],
    gen: (rng) => twoStep(randInt(rng, 2, 20), "+", randInt(rng, 2, 9), "+", randInt(rng, 2, 9)),
  },

  // ── Taking away ────────────────────────────────────────────────────────
  {
    id: "sub.within10",
    op: "sub",
    tier: 1,
    label: "Taking away within 10",
    limit: 10,
    prereqs: [],
    gen: (rng) => {
      const a = randInt(rng, 0, 10);
      return binary("−", a, randInt(rng, 0, a));
    },
  },
  {
    id: "sub.minus12",
    op: "sub",
    tier: 1,
    label: "Taking away 1 or 2",
    limit: 10,
    prereqs: [],
    gen: (rng) => binary("−", randInt(rng, 2, 10), randInt(rng, 1, 2)),
  },
  {
    id: "sub.from10",
    op: "sub",
    tier: 1,
    label: "Taking away from 10",
    limit: 10,
    prereqs: [],
    gen: (rng) => binary("−", 10, randInt(rng, 0, 10)),
  },
  {
    id: "sub.within20",
    op: "sub",
    tier: 2,
    label: "Taking away within 20",
    limit: 20,
    prereqs: ["sub.within10"],
    gen: (rng) => {
      const a = randInt(rng, 11, 20);
      return binary("−", a, randInt(rng, 1, a));
    },
  },
  {
    id: "sub.missingTo20",
    op: "sub",
    tier: 3,
    label: "Missing number to 20",
    limit: 20,
    prereqs: ["sub.within10"],
    gen: (rng) => {
      const a = randInt(rng, 5, 20);
      return binary("−", a, randInt(rng, 0, a), 1);
    },
  },
  {
    id: "sub.from20",
    op: "sub",
    tier: 2,
    label: "Counting back from 20",
    limit: 20,
    prereqs: ["sub.from10"],
    gen: (rng) => binary("−", 20, randInt(rng, 1, 20)),
  },
  {
    id: "sub.2d1d",
    op: "sub",
    tier: 3,
    label: "2-digit − 1-digit, exchanging",
    limit: 100,
    prereqs: ["sub.within20"],
    gen: (rng) => {
      const a = randInt(rng, 2, 9) * 10 + randInt(rng, 0, 7);
      return binary("−", a, randInt(rng, (a % 10) + 1, 9));
    },
  },
  {
    id: "sub.tens",
    op: "sub",
    tier: 3,
    label: "Taking away tens",
    limit: 100,
    prereqs: ["sub.within10"],
    gen: (rng) => {
      const a = randInt(rng, 2, 10);
      return binary("−", a * 10, randInt(rng, 1, a) * 10);
    },
  },
  {
    id: "sub.2d2d",
    op: "sub",
    tier: 4,
    label: "2-digit − 2-digit",
    limit: 100,
    prereqs: ["sub.2d1d", "sub.tens"],
    gen: (rng) => {
      const a = randInt(rng, 30, 99);
      return binary("−", a, randInt(rng, 11, a - 1));
    },
  },
  {
    id: "sub.missingStart",
    op: "sub",
    tier: 4,
    label: "Missing starting number",
    limit: 100,
    prereqs: ["sub.missingTo20", "sub.2d1d"],
    gen: (rng) => {
      const b = randInt(rng, 10, 50);
      return binary("−", b + randInt(rng, 10, 99 - b), b, 0);
    },
  },
  {
    id: "sub.twoStep",
    op: "sub",
    tier: 5,
    label: "Taking away twice",
    limit: 50,
    prereqs: ["sub.within20"],
    gen: (rng) => {
      const b = randInt(rng, 2, 9);
      const c = randInt(rng, 2, 9);
      return twoStep(randInt(rng, b + c, 30), "−", b, "−", c);
    },
  },

  // ── Times ──────────────────────────────────────────────────────────────
  { ...table("mul", 1, 1), label: "1 times table" },
  table("mul", 2, 1),
  table("mul", 5, 1),
  table("mul", 10, 1),
  table("mul", 3, 2),
  table("mul", 4, 2),
  table("mul", 6, 3),
  table("mul", 7, 3),
  table("mul", 8, 3),
  table("mul", 9, 3),
  {
    id: "mul.missingFactor",
    op: "mul",
    tier: 4,
    label: "Missing factor",
    limit: 10,
    prereqs: ["mul.x6", "mul.x7", "mul.x8", "mul.x9"],
    gen: (rng) => {
      const [a, b] = eitherOrder(rng, randInt(rng, 2, 10), randInt(rng, 1, 10));
      return binary("×", a, b, rng.next() < 0.5 ? 0 : 1);
    },
  },
  {
    id: "mul.x11",
    op: "mul",
    tier: 4,
    label: "11 times table",
    limit: 11,
    prereqs: ["mul.x10"],
    gen: (rng) => {
      const [a, b] = eitherOrder(rng, 11, randInt(rng, 0, 12));
      return binary("×", a, b);
    },
  },
  {
    id: "mul.x12",
    op: "mul",
    tier: 4,
    label: "12 times table",
    limit: 12,
    prereqs: ["mul.x6"],
    gen: (rng) => {
      const [a, b] = eitherOrder(rng, 12, randInt(rng, 0, 12));
      return binary("×", a, b);
    },
  },
  {
    id: "mul.2d1d",
    op: "mul",
    tier: 5,
    label: "2-digit × 1-digit",
    limit: 10,
    prereqs: ["mul.x6", "mul.x7", "mul.x8", "mul.x9"],
    gen: (rng) => binary("×", randInt(rng, 11, 49), randInt(rng, 2, 9)),
  },

  // ── Sharing ────────────────────────────────────────────────────────────
  table("div", 2, 1),
  table("div", 5, 1),
  table("div", 10, 1),
  table("div", 3, 2),
  table("div", 4, 2),
  table("div", 6, 3),
  table("div", 7, 3),
  table("div", 8, 3),
  table("div", 9, 3),
  {
    id: "div.missingDivisor",
    op: "div",
    tier: 4,
    label: "Missing divisor",
    limit: 10,
    prereqs: ["div.x6", "div.x7", "div.x8", "div.x9"],
    gen: (rng) => {
      const t = randInt(rng, 2, 10);
      const n = randInt(rng, 1, 10);
      return binary("÷", t * n, t, 1);
    },
  },
  {
    id: "div.missingDividend",
    op: "div",
    tier: 4,
    label: "Missing number to share",
    limit: 10,
    prereqs: ["div.x6", "div.x7", "div.x8", "div.x9"],
    gen: (rng) => {
      const t = randInt(rng, 2, 10);
      return binary("÷", t * randInt(rng, 0, 10), t, 0);
    },
  },
  {
    id: "div.2d1d",
    op: "div",
    tier: 5,
    label: "2-digit ÷ 1-digit",
    limit: 10,
    prereqs: ["div.x6", "div.x7", "div.x8", "div.x9"],
    gen: (rng) => {
      const t = randInt(rng, 2, 9);
      return binary("÷", t * randInt(rng, 11, Math.floor(99 / t)), t);
    },
  },
];

const TypeMetaSchema = z
  .array(
    z.object({
      id: z.string().regex(/^(add|sub|mul|div)\.[A-Za-z0-9]+$/),
      op: z.enum(OPS),
      tier: z
        .number()
        .int()
        .min(TIERS[0])
        .max(TIERS[TIERS.length - 1]!),
      label: z.string().min(1),
      limit: z.number().int().positive(),
      prereqs: z.array(z.string()),
    }),
  )
  .refine((ts) => new Set(ts.map((t) => t.id)).size === ts.length, "type ids must be unique")
  .refine(
    (ts) => ts.every((t) => t.prereqs.every((p) => ts.some((u) => u.id === p))),
    "every prereq must be a known type id",
  )
  .refine((ts) => ts.every((t) => t.id.startsWith(`${t.op}.`)), "type ids start with their op");

TypeMetaSchema.parse(DEFS);

export const QUESTION_TYPES: readonly QuestionType[] = DEFS.map((d) => ({ ...d, points: d.tier }));

const BY_ID = new Map(QUESTION_TYPES.map((t) => [t.id, t]));

export function questionType(id: string): QuestionType | undefined {
  return BY_ID.get(id);
}

export function typesFor(ops: readonly Op[]): QuestionType[] {
  return QUESTION_TYPES.filter((t) => ops.includes(t.op));
}

export function makeQuestion(type: QuestionType, rng: Rng): Question {
  return { typeId: type.id, op: type.op, tier: type.tier, points: type.points, ...type.gen(rng) };
}
